import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../component/Header';
import Footer from '../component/footer';
import Input from '../component/Input';
import { login, signup } from '../services/authService';
import { setAuthenticated, setAuthToken } from '../utils/authSession';
import { showErrorToast, showSuccessToast } from '../utils/toaster';

const extractToken = (response: unknown): string | null => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const payload = response as Record<string, unknown>;
  const directToken = payload.token || payload.access_token || payload.jwt;

  if (typeof directToken === 'string' && directToken.trim()) {
    return directToken;
  }

  const nestedData = payload.data;
  if (nestedData && typeof nestedData === 'object') {
    const nested = nestedData as Record<string, unknown>;
    const nestedToken = nested.token || nested.access_token || nested.jwt;
    if (typeof nestedToken === 'string' && nestedToken.trim()) {
      return nestedToken;
    }
  }

  return null;
};

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'signup');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });

  const updateField = (
    field: 'firstName' | 'lastName' | 'email' | 'password',
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAuthSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const email = formData.email.trim();
    const password = formData.password;
    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const name = `${firstName} ${lastName}`.trim();

    if (!email || !password || (!isLogin && (!firstName || !lastName))) {
      showErrorToast('Please fill all required fields.');
      return;
    }

    try {
      setIsSubmitting(true);

      let response;

      if (isLogin) {
        response = await login({ email, password });
        showSuccessToast(response.message || 'Login successful.');
      } else {
        response = await signup({ name, email, password });
        showSuccessToast(response.message || 'Signup successful.');
      }

      const token = extractToken(response);
      if (token) {
        setAuthToken(token, email);
      } else {
        setAuthenticated(email);
      }

      setFormData({ firstName: '', lastName: '', email: '', password: '' });
      navigate('/pricing');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Request failed. Please try again.';
      showErrorToast(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-[100vh] flex items-center justify-center bg-[#113A28] px-4 py-16">
        <div className="w-full max-w-md p-8 bg-white shadow-2xl rounded-3xl md:p-12">
          <h2 className="text-3xl font-bold text-[#113A28] mb-2 text-center">
            {isLogin ? 'Login to Your Account' : 'Create Your Account'}
          </h2>
          <p className="mb-8 text-center text-gray-500">
            {isLogin ? 'Welcome back! Please login to continue.' : 'Sign up to start learning with us.'}
          </p>
          <form className="space-y-6" onSubmit={handleAuthSubmit}>
            {!isLogin && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  id="firstName"
                  label="First Name"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={(event) => updateField('firstName', event.target.value)}
                  required
                />
                <Input
                  id="lastName"
                  label="Last Name"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={(event) => updateField('lastName', event.target.value)}
                  required
                />
              </div>
            )}
            <Input
              id="email"
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(event) => updateField('email', event.target.value)}
              required
            />
            <Input
              id="password"
              label="Password"
              type="password"
              placeholder="********"
              value={formData.password}
              onChange={(event) => updateField('password', event.target.value)}
              required
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 bg-[#4CE470] text-[#113A28] px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#3bc45d] transition-colors shadow-lg"
            >
              {isSubmitting ? 'Please wait...' : isLogin ? 'Login' : 'Sign Up'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <button
              className="text-[#4CE470] font-bold hover:underline"
              onClick={() => setIsLogin(!isLogin)}
              type="button"
            >
              {isLogin
                ? "Don't have an account? Sign Up"
                : 'Already have an account? Login'}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Auth;
